import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import axios from 'axios';
import { Users, Package, CheckCircle, XCircle, Eye, DollarSign, Edit, Trash2, Shield, BarChart3, KeyRound, Copy, Check, FileText, Zap, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { rate: exchangeRate, updateExchangeRate } = useExchangeRate();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [carRequests, setCarRequests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [boosts, setBoosts] = useState([]);
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [paymentDealerFilter, setPaymentDealerFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [pendingDealers, setPendingDealers] = useState([]);
  const [viewingDocs, setViewingDocs] = useState(null);
  const [newExchangeRate, setNewExchangeRate] = useState('');
  const [updatingRate, setUpdatingRate] = useState(false);
  
  const [siteSettings, setSiteSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Payout Subaccounts (Paystack split payments)
  const [pendingSubaccounts, setPendingSubaccounts] = useState([]);
  const [activeSubaccounts, setActiveSubaccounts] = useState([]);
  const [provisioningId, setProvisioningId] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/dashboard');
    } else if (user && user.role === 'admin') {
      fetchAdminData();
    }
  }, [user, authLoading, navigate]);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAdminData = async () => {
    try {
      const [statsRes, dealersRes, vehiclesRes, transactionsRes, usersRes, requestsRes, reservationsRes, paymentsRes, paymentStatsRes, boostsRes, pendingDealersRes, settingsRes, pendingSubRes, activeSubRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/dealers`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/vehicles`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/transactions`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/vehicle-requests`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/reservations`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/payments`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/payments/stats`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/boosts`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/dealers/pending-verification`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/site-settings`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/dealers/pending-subaccounts`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/dealers/with-subaccounts`, { headers: getHeaders(), withCredentials: true })
      ]);
      setStats(statsRes.data);
      setDealers(dealersRes.data);
      setVehicles(vehiclesRes.data);
      setTransactions(transactionsRes.data);
      setAllUsers(usersRes.data);
      setCarRequests(requestsRes.data || []);
      setReservations(reservationsRes.data || []);
      setPayments(paymentsRes.data || []);
      setPaymentStats(paymentStatsRes.data || {});
      setBoosts(boostsRes.data || []);
      setPendingDealers(pendingDealersRes.data || []);
      setSiteSettings(settingsRes.data || {});
      setPendingSubaccounts(pendingSubRes.data?.dealers || []);
      setActiveSubaccounts(activeSubRes.data?.dealers || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const provisionSubaccount = async (userId) => {
    setProvisioningId(userId);
    try {
      const res = await axios.post(
        `${API}/admin/dealers/${userId}/create-subaccount`,
        {},
        { headers: getHeaders(), withCredentials: true }
      );
      alert(res.data?.message || 'Subaccount created');
      fetchAdminData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create subaccount');
    } finally {
      setProvisioningId(null);
    }
  };

  const approveDealer = async (dealerId) => {
    try {
      await axios.post(`${API}/admin/dealers/${dealerId}/approve`, {}, { headers: getHeaders(), withCredentials: true });
      alert('Dealer approved!');
      fetchAdminData();
    } catch (error) {
      alert('Failed to approve dealer');
    }
  };

  const rejectDealer = async (dealerId) => {
    try {
      await axios.post(`${API}/admin/dealers/${dealerId}/reject`, {}, { headers: getHeaders(), withCredentials: true });
      alert('Dealer rejected');
      fetchAdminData();
    } catch (error) {
      alert('Failed to reject dealer');
    }
  };

  const verifyDealer = async (userId, approved, notes = '') => {
    try {
      const requestBody = approved ? {} : { admin_notes: notes };
      await axios.post(
        `${API}/admin/dealers/${userId}/verify?approved=${approved}`,
        requestBody,
        { headers: getHeaders(), withCredentials: true }
      );
      alert(`Dealer ${approved ? 'verified' : 'rejected'} successfully!`);
      fetchAdminData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update verification status');
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/vehicle-requests/${requestId}/status`, 
        { status: newStatus }, 
        { headers: getHeaders(), withCredentials: true }
      );
      fetchAdminData();
    } catch (error) {
      alert('Failed to update request status');
    }
  };

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      await axios.patch(`${API}/reservations/${reservationId}/status?status=${newStatus}`,
        {},
        { headers: getHeaders(), withCredentials: true }
      );
      fetchAdminData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update reservation status');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/role?new_role=${newRole}`, {}, { headers: getHeaders(), withCredentials: true });
      alert(`User role updated to ${newRole}`);
      fetchAdminData();
    } catch (error) {
      alert('Failed to update user role');
    }
  };

  const handleUpdateExchangeRate = async () => {
    const rate = parseFloat(newExchangeRate);
    
    if (!rate || rate <= 0) {
      alert('Please enter a valid exchange rate');
      return;
    }
    
    if (rate < 100 || rate > 10000) {
      alert('Exchange rate seems invalid. Please check the value.');
      return;
    }
    
    setUpdatingRate(true);
    try {
      const result = await updateExchangeRate(rate);
      if (result.success) {
        alert(result.message);
        setNewExchangeRate('');
      } else {
        alert(result.message);
      }
    } catch (error) {
      alert('Failed to update exchange rate');
    } finally {
      setUpdatingRate(false);
    }
  };


  const handleSaveSiteSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await axios.put(`${API}/admin/site-settings`, siteSettings, {
        headers: getHeaders(),
        withCredentials: true
      });
      alert(response.data?.message || 'Site settings saved successfully!');
    } catch (error) {
      console.error('Failed to save site settings:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`Failed to save site settings: ${errorMsg}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setSiteSettings({ ...siteSettings, [field]: value });
  };

  const handleDeleteVehicle = async (vehicleId) => {
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { headers: getHeaders(), withCredentials: true });
      alert('Vehicle deleted!');
      setDeleteConfirm(null);
      fetchAdminData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { headers: getHeaders(), withCredentials: true });
      setResetModal(res.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate reset token');
    }
  };

  const copyResetLink = () => {
    const link = `${window.location.origin}/reset-password?token=${resetModal.reset_token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'verification', label: `Verification (${pendingDealers.length})` },
    { id: 'payments', label: `Payments (${payments.length})` },
    { id: 'subaccounts', label: `Payout Subaccounts (${pendingSubaccounts.length})` },
    { id: 'featured', label: `Featured Listings (${boosts.length})` },
    { id: 'dealers', label: `Dealers (${dealers.length})` },
    { id: 'vehicles', label: `Vehicles (${vehicles.length})` },
    { id: 'users', label: `Users (${allUsers.length})` },
    { id: 'requests', label: `Car Requests (${carRequests.length})` },
    { id: 'reservations', label: `Reservations (${reservations.length})` },
    { id: 'transactions', label: `Transactions (${transactions.length})` },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-heading font-bold" data-testid="admin-dashboard-title">Admin Dashboard</h1>
          </div>
          <button
            onClick={() => navigate('/admin/csv-import')}
            className="flex items-center space-x-2 px-4 py-2 bg-primary hover:bg-primary/80 text-white font-heading font-bold rounded transition-colors"
          >
            <Package className="w-4 h-4" />
            <span>Import CSV</span>
          </button>
        </div>

        {/* Stats Row */}
        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass p-5 rounded-sm">
              <Users className="w-6 h-6 text-primary mb-2" />
              <div className="text-2xl font-heading font-bold">{stats.total_users}</div>
              <div className="text-xs text-textSecondary">Users</div>
            </div>
            <div className="glass p-5 rounded-sm">
              <Package className="w-6 h-6 text-primary mb-2" />
              <div className="text-2xl font-heading font-bold">{stats.total_vehicles}</div>
              <div className="text-xs text-textSecondary">Vehicles</div>
            </div>
            <div className="glass p-5 rounded-sm">
              <BarChart3 className="w-6 h-6 text-primary mb-2" />
              <div className="text-2xl font-heading font-bold">{stats.active_auctions}</div>
              <div className="text-xs text-textSecondary">Active Auctions</div>
            </div>
            <div className="glass p-5 rounded-sm">
              <DollarSign className="w-6 h-6 text-green-500 mb-2" />
              <div className="text-2xl font-heading font-bold text-green-500">
                {paymentStats ? `₦${paymentStats.total_revenue.toLocaleString()}` : 'Loading...'}
              </div>
              <div className="text-xs text-textSecondary">Total Revenue (NGN)</div>
            </div>
            <div className="glass p-5 rounded-sm">
              <DollarSign className="w-6 h-6 text-primary mb-2" />
              <div className="text-2xl font-heading font-bold">
                {paymentStats ? paymentStats.total_successful_payments : 0}
              </div>
              <div className="text-xs text-textSecondary">Successful Payments</div>
            </div>
          </div>

          {/* Payment Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="glass p-6 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-textSecondary">Pending Payments</span>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              </div>
              <div className="text-3xl font-heading font-bold">
                {paymentStats ? paymentStats.pending_payments : 0}
              </div>
              <div className="text-xs text-textSecondary mt-1">Awaiting completion</div>
            </div>
            <div className="glass p-6 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-textSecondary">Successful Payments</span>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="text-3xl font-heading font-bold text-green-500">
                {paymentStats ? paymentStats.total_successful_payments : 0}
              </div>
              <div className="text-xs text-textSecondary mt-1">
                Revenue: {paymentStats ? `₦${paymentStats.total_revenue.toLocaleString()}` : '₦0'}
              </div>
            </div>
            <div className="glass p-6 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-textSecondary">Failed Payments</span>
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              </div>
              <div className="text-3xl font-heading font-bold text-red-500">
                {paymentStats ? paymentStats.failed_payments : 0}
              </div>
              <div className="text-xs text-textSecondary mt-1">Require attention</div>
            </div>
          </div>
          </>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-white/10 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-4 font-heading font-bold text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'text-primary border-b-2 border-primary' : 'text-textSecondary hover:text-white'
              }`} data-testid={`tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="glass p-6 rounded-sm">
              <h2 className="text-lg font-heading font-bold mb-4">Pending Dealer Applications</h2>
              {dealers.filter(d => !d.verified).length === 0 ? (
                <p className="text-textSecondary text-sm">No pending applications</p>
              ) : (
                dealers.filter(d => !d.verified).map(dealer => (
                  <div key={dealer.dealer_id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <div>
                      <div className="font-bold">{dealer.company_name}</div>
                      <div className="text-sm text-textSecondary">{dealer.phone || 'No phone'} | {dealer.address || 'No address'}</div>
                    </div>
                    <button onClick={() => approveDealer(dealer.dealer_id)} data-testid={`approve-dealer-${dealer.dealer_id}`}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold px-4 py-2 text-sm flex items-center space-x-1 transition-colors">
                      <CheckCircle className="w-4 h-4" /><span>Approve</span>
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="glass p-6 rounded-sm">
              <h2 className="text-lg font-heading font-bold mb-4">Recent Vehicles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {vehicles.slice(0, 6).map(v => (
                  <div key={v.vehicle_id} className="flex items-center space-x-3 p-3 bg-surfaceHighlight rounded-sm">
                    <img src={v.images[0]?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${v.images[0]}` : v.images[0]}
                      alt="" className="w-16 h-12 object-cover rounded-sm" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{v.year} {v.make} {v.model}</div>
                      <div className="text-xs text-textSecondary">{v.condition}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Dealers Tab */}

        {/* Verification Tab */}
        {activeTab === 'verification' && (
          <div className="space-y-6">
            <div className="glass rounded-sm overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-xl font-heading font-bold">Pending Dealer Verification</h2>
                <p className="text-sm text-textSecondary mt-1">Review and approve dealer verification documents</p>
              </div>
              
              {pendingDealers.length === 0 ? (
                <div className="p-12 text-center text-textSecondary">
                  <Shield className="w-16 h-16 mx-auto mb-4 text-textSecondary opacity-50" />
                  <p>No dealers pending verification</p>
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {pendingDealers.map((dealer) => (
                    <div key={dealer.user_id} className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-heading font-bold">{dealer.name}</h3>
                            <span className="px-3 py-1 bg-yellow-600 rounded-sm text-xs font-bold uppercase">
                              Pending Verification
                            </span>
                          </div>
                          <p className="text-sm text-textSecondary mb-1">Email: {dealer.email}</p>
                          <p className="text-sm text-textSecondary">
                            Submitted: {new Date(dealer.verification_documents?.[0]?.uploaded_at || dealer.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Documents */}
                      <div className="mb-4">
                        <h4 className="text-sm font-bold mb-3">Uploaded Documents ({dealer.verification_documents?.length || 0})</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {(dealer.verification_documents || []).map((doc, idx) => (
                            <div key={idx} className="border border-white/20 rounded-sm p-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <FileText className="w-4 h-4 text-primary" />
                                <span className="text-sm font-bold">
                                  {doc.type.replace(/_/g, ' ').toUpperCase()}
                                </span>
                              </div>
                              <p className="text-xs text-textSecondary mb-2 truncate">{doc.original_filename}</p>
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/dealer_documents/${doc.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1 text-xs text-primary hover:text-primaryHover"
                              >
                                <Eye className="w-3 h-3" />
                                <span>View Document</span>
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-3 pt-4 border-t border-white/10">
                        <button
                          onClick={() => verifyDealer(dealer.user_id, true)}
                          className="flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold transition-colors"
                        >
                          <CheckCircle className="w-5 h-5" />
                          <span>Approve Dealer</span>
                        </button>
                        <button
                          onClick={() => {
                            const notes = prompt('Rejection reason (optional):');
                            verifyDealer(dealer.user_id, false, notes || '');
                          }}
                          className="flex items-center space-x-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold transition-colors"
                        >
                          <XCircle className="w-5 h-5" />
                          <span>Reject</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Payment Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="glass p-6 rounded-sm">
                <DollarSign className="w-8 h-8 text-green-500 mb-4" />
                <div className="text-3xl font-heading font-bold text-green-500">
                  ₦{paymentStats ? paymentStats.total_revenue.toLocaleString() : '0'}
                </div>
                <div className="text-sm text-textSecondary">Total Revenue (NGN)</div>
              </div>
              <div className="glass p-6 rounded-sm">
                <CheckCircle className="w-8 h-8 text-green-500 mb-4" />
                <div className="text-3xl font-heading font-bold">
                  {paymentStats ? paymentStats.total_successful_payments : 0}
                </div>
                <div className="text-sm text-textSecondary">Successful</div>
              </div>
              <div className="glass p-6 rounded-sm">
                <Package className="w-8 h-8 text-yellow-500 mb-4" />
                <div className="text-3xl font-heading font-bold">
                  {paymentStats ? paymentStats.pending_payments : 0}
                </div>
                <div className="text-sm text-textSecondary">Pending</div>
              </div>
              <div className="glass p-6 rounded-sm">
                <XCircle className="w-8 h-8 text-red-500 mb-4" />
                <div className="text-3xl font-heading font-bold">
                  {paymentStats ? paymentStats.failed_payments : 0}
                </div>
                <div className="text-sm text-textSecondary">Failed</div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="glass p-6 rounded-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-2">Search</label>
                  <input
                    type="text"
                    placeholder="Buyer, dealer, vehicle, or reference..."
                    className="w-full glass rounded-sm px-4 py-2 text-sm border border-white/10 focus:border-primary focus:outline-none"
                    value={paymentSearch}
                    onChange={(e) => setPaymentSearch(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-2">Status Filter</label>
                  <select
                    className="w-full glass rounded-sm px-4 py-2 text-sm border border-white/10 focus:border-primary focus:outline-none"
                    value={paymentStatusFilter}
                    onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="successful">Successful</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-2">Dealer Filter</label>
                  <select
                    className="w-full glass rounded-sm px-4 py-2 text-sm border border-white/10 focus:border-primary focus:outline-none"
                    value={paymentDealerFilter}
                    onChange={(e) => setPaymentDealerFilter(e.target.value)}
                  >
                    <option value="all">All Dealers</option>
                    {Array.from(new Set(payments.map(p => p.dealer_details?.name).filter(Boolean))).map(dealerName => (
                      <option key={dealerName} value={dealerName}>{dealerName}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Payments Table */}
            <div className="glass rounded-sm overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-xl font-heading font-bold">All Payment Transactions</h2>
                <p className="text-sm text-textSecondary mt-1">
                  {(() => {
                    const filtered = payments.filter(payment => {
                      const matchesSearch = !paymentSearch || 
                        payment.buyer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.buyer_details?.email?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.dealer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.vehicle_details?.make?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.vehicle_details?.model?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.paystack_reference?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                        payment.reference?.toLowerCase().includes(paymentSearch.toLowerCase());
                      
                      const matchesStatus = paymentStatusFilter === 'all' || payment.status === paymentStatusFilter;
                      const matchesDealer = paymentDealerFilter === 'all' || payment.dealer_details?.name === paymentDealerFilter;
                      
                      return matchesSearch && matchesStatus && matchesDealer;
                    });
                    return `Showing ${filtered.length} of ${payments.length} transactions`;
                  })()}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surfaceHighlight">
                    <tr>
                      <th className="p-4 text-left font-heading text-sm">Buyer</th>
                      <th className="p-4 text-left font-heading text-sm">Dealer</th>
                      <th className="p-4 text-left font-heading text-sm">Vehicle</th>
                      <th className="p-4 text-left font-heading text-sm">Amount</th>
                      <th className="p-4 text-left font-heading text-sm">Payment Status</th>
                      <th className="p-4 text-left font-heading text-sm">Reservation</th>
                      <th className="p-4 text-left font-heading text-sm">Date</th>
                      <th className="p-4 text-left font-heading text-sm">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments
                      .filter(payment => {
                        const matchesSearch = !paymentSearch || 
                          payment.buyer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.buyer_details?.email?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.dealer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.vehicle_details?.make?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.vehicle_details?.model?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.paystack_reference?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                          payment.reference?.toLowerCase().includes(paymentSearch.toLowerCase());
                        
                        const matchesStatus = paymentStatusFilter === 'all' || payment.status === paymentStatusFilter;
                        const matchesDealer = paymentDealerFilter === 'all' || payment.dealer_details?.name === paymentDealerFilter;
                        
                        return matchesSearch && matchesStatus && matchesDealer;
                      })
                      .map((payment) => {
                        const getPaymentStatusColor = (status) => {
                          switch(status) {
                            case 'successful': return 'bg-green-600';
                            case 'pending': return 'bg-yellow-600';
                            case 'failed': return 'bg-red-600';
                            default: return 'bg-gray-600';
                          }
                        };

                        const getReservationStatusColor = (status) => {
                          switch(status) {
                            case 'approved': return 'text-green-500';
                            case 'completed': return 'text-blue-500';
                            case 'pending': return 'text-yellow-500';
                            case 'rejected': return 'text-red-500';
                            default: return 'text-gray-500';
                          }
                        };

                        return (
                          <tr key={payment.payment_id} className="border-t border-white/10 hover:bg-surfaceHighlight transition-colors">
                            <td className="p-4">
                              <div className="font-bold">{payment.buyer_details?.name || 'Unknown'}</div>
                              <div className="text-xs text-textSecondary">{payment.buyer_details?.email}</div>
                            </td>
                            <td className="p-4">
                              <div className="font-medium">{payment.dealer_details?.name || 'N/A'}</div>
                              <div className="text-xs text-textSecondary">{payment.dealer_details?.email || ''}</div>
                            </td>
                            <td className="p-4">
                              <div className="font-bold text-sm">
                                {payment.vehicle_details?.year} {payment.vehicle_details?.make} {payment.vehicle_details?.model}
                              </div>
                              <div className="text-xs text-textSecondary font-mono">
                                ID: {payment.vehicle_details?.vehicle_id || payment.vehicle_id}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="font-bold text-green-500">
                                ₦{payment.amount.toLocaleString()}
                              </div>
                              <div className="text-xs text-textSecondary">
                                {payment.currency || 'NGN'}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${getPaymentStatusColor(payment.status)}`}>
                                {payment.status}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`text-sm font-medium uppercase ${getReservationStatusColor(payment.reservation_details?.status)}`}>
                                {payment.reservation_details?.status || 'N/A'}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">
                                {new Date(payment.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="text-xs text-textSecondary">
                                {new Date(payment.created_at).toLocaleTimeString('en-US', { 
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-xs font-mono text-textSecondary break-all max-w-[200px]">
                                {payment.paystack_reference || payment.reference}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {payments.filter(payment => {
                  const matchesSearch = !paymentSearch || 
                    payment.buyer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.buyer_details?.email?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.dealer_details?.name?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.vehicle_details?.make?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.vehicle_details?.model?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.paystack_reference?.toLowerCase().includes(paymentSearch.toLowerCase()) ||
                    payment.reference?.toLowerCase().includes(paymentSearch.toLowerCase());
                  
                  const matchesStatus = paymentStatusFilter === 'all' || payment.status === paymentStatusFilter;
                  const matchesDealer = paymentDealerFilter === 'all' || payment.dealer_details?.name === paymentDealerFilter;
                  
                  return matchesSearch && matchesStatus && matchesDealer;
                }).length === 0 && (
                  <div className="p-12 text-center text-textSecondary">
                    <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>{payments.length === 0 ? 'No payment transactions yet' : 'No matching transactions found'}</p>
                    {paymentSearch || paymentStatusFilter !== 'all' || paymentDealerFilter !== 'all' ? (
                      <button
                        onClick={() => {
                          setPaymentSearch('');
                          setPaymentStatusFilter('all');
                          setPaymentDealerFilter('all');
                        }}
                        className="mt-4 text-primary hover:text-primaryHover"
                      >
                        Clear filters
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'dealers' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold mb-4">Dealer Accounts</h2>
            {allUsers.filter(u => u.role === 'dealer').length === 0 ? (
              <div className="glass p-8 text-center text-textSecondary">No dealer accounts yet</div>
            ) : (
              <>
                {/* Pending Dealers */}
                {allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'pending').length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-heading font-bold mb-3 text-yellow-500">Pending Approval ({allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'pending').length})</h3>
                    {allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'pending').map(dealer => (
                      <div key={dealer.user_id} className="glass p-5 rounded-sm mb-3 border-l-4 border-yellow-500">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-heading font-bold text-lg">{dealer.name}</h3>
                              <span className="px-2 py-0.5 bg-yellow-600 rounded-sm text-xs font-bold">PENDING</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-textSecondary">
                              <div>Email: {dealer.email}</div>
                              <div>Registered: {new Date(dealer.created_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button onClick={() => approveDealer(dealer.user_id)}
                              className="bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold px-4 py-2 text-sm flex items-center space-x-1 transition-colors">
                              <CheckCircle className="w-4 h-4" /><span>Approve</span>
                            </button>
                            <button onClick={() => rejectDealer(dealer.user_id)}
                              className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold px-4 py-2 text-sm flex items-center space-x-1 transition-colors">
                              <XCircle className="w-4 h-4" /><span>Reject</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Approved Dealers */}
                {allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'approved').length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-heading font-bold mb-3 text-green-500">Approved Dealers ({allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'approved').length})</h3>
                    {allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'approved').map(dealer => (
                      <div key={dealer.user_id} className="glass p-5 rounded-sm mb-3 border-l-4 border-green-500">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-heading font-bold text-lg">{dealer.name}</h3>
                              <span className="px-2 py-0.5 bg-green-600 rounded-sm text-xs font-bold">APPROVED</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-textSecondary">
                              <div>Email: {dealer.email}</div>
                              <div>Registered: {new Date(dealer.created_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Rejected Dealers */}
                {allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'rejected').length > 0 && (
                  <div>
                    <h3 className="text-lg font-heading font-bold mb-3 text-red-500">Rejected Dealers ({allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'rejected').length})</h3>
                    {allUsers.filter(u => u.role === 'dealer' && u.approval_status === 'rejected').map(dealer => (
                      <div key={dealer.user_id} className="glass p-5 rounded-sm mb-3 border-l-4 border-red-500">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-heading font-bold text-lg">{dealer.name}</h3>
                              <span className="px-2 py-0.5 bg-red-600 rounded-sm text-xs font-bold">REJECTED</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-textSecondary">
                              <div>Email: {dealer.email}</div>
                              <div>Registered: {new Date(dealer.created_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <button onClick={() => approveDealer(dealer.user_id)}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold px-4 py-2 text-sm flex items-center space-x-1 transition-colors ml-4">
                            <CheckCircle className="w-4 h-4" /><span>Re-approve</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-bold">All Vehicles ({vehicles.length})</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {vehicles.map(vehicle => (
                <div key={vehicle.vehicle_id} className="glass rounded-sm overflow-hidden" data-testid={`admin-vehicle-${vehicle.vehicle_id}`}>
                  <img src={vehicle.images[0]?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}` : vehicle.images[0]}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} className="w-full aspect-video object-cover" />
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-lg mb-1">{vehicle.year} {vehicle.make} {vehicle.model}</h3>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-bold uppercase ${
                        vehicle.condition === 'clean' ? 'bg-green-600' : vehicle.condition === 'salvage' ? 'bg-yellow-600' : 'bg-red-600'
                      }`}>{vehicle.condition}</span>
                      <span className="text-xs text-textSecondary">By: {vehicle.seller_name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a href={`/vehicle/${vehicle.vehicle_id}`}
                        className="flex-1 bg-surfaceHighlight hover:bg-white/10 text-white rounded-sm font-medium px-3 py-2 text-center text-sm transition-colors flex items-center justify-center space-x-1">
                        <Eye className="w-3.5 h-3.5" /><span>View</span>
                      </a>
                      {deleteConfirm === vehicle.vehicle_id ? (
                        <div className="flex items-center space-x-1">
                          <button onClick={() => handleDeleteVehicle(vehicle.vehicle_id)}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-medium px-3 py-2 text-xs transition-colors" data-testid={`confirm-del-${vehicle.vehicle_id}`}>Confirm</button>
                          <button onClick={() => setDeleteConfirm(null)}
                            className="bg-gray-600 hover:bg-gray-700 text-white rounded-sm font-medium px-3 py-2 text-xs transition-colors">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirm(vehicle.vehicle_id)} data-testid={`admin-delete-${vehicle.vehicle_id}`}
                          className="bg-red-600/80 hover:bg-red-600 text-white rounded-sm font-medium px-3 py-2 text-sm transition-colors flex items-center space-x-1">
                          <Trash2 className="w-3.5 h-3.5" /><span>Delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold mb-4">All Users ({allUsers.length})</h2>
            <div className="glass rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-sm font-heading text-textSecondary">Name</th>
                      <th className="text-left p-4 text-sm font-heading text-textSecondary">Email</th>
                      <th className="text-left p-4 text-sm font-heading text-textSecondary">Role</th>
                      <th className="text-left p-4 text-sm font-heading text-textSecondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map(u => (
                      <tr key={u.user_id} className="border-b border-white/5" data-testid={`user-row-${u.user_id}`}>
                        <td className="p-4 text-sm">{u.name}</td>
                        <td className="p-4 text-sm text-textSecondary">{u.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded-sm text-xs font-bold uppercase ${
                            u.role === 'admin' ? 'bg-primary' : u.role === 'dealer' ? 'bg-blue-600' : 'bg-gray-600'
                          }`}>{u.role}</span>
                        </td>
                        <td className="p-4">
                          {u.user_id !== user?.user_id && (
                            <div className="flex items-center space-x-2">
                              <select value={u.role} onChange={(e) => updateUserRole(u.user_id, e.target.value)}
                                className="bg-surfaceHighlight border border-white/10 text-white text-xs rounded-sm px-2 py-1"
                                data-testid={`role-select-${u.user_id}`}>
                                <option value="buyer">Buyer</option>
                                <option value="dealer">Dealer</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button onClick={() => handleResetPassword(u.user_id)}
                                data-testid={`reset-pw-${u.user_id}`}
                                className="bg-yellow-600/80 hover:bg-yellow-600 text-white rounded-sm text-xs font-bold px-2 py-1 flex items-center space-x-1 transition-colors whitespace-nowrap">
                                <KeyRound className="w-3 h-3" /><span>Reset PW</span>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold mb-4">All Transactions</h2>
            {transactions.length === 0 ? (
              <div className="glass p-8 text-center text-textSecondary">No transactions yet</div>
            ) : (
              transactions.map(txn => (
                <div key={txn.transaction_id} className="glass p-5 rounded-sm">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div>
                      <div className="text-textSecondary text-xs">ID</div>
                      <div className="font-mono text-xs">{txn.transaction_id}</div>
                    </div>
                    <div>
                      <div className="text-textSecondary text-xs">User</div>
                      <div>{txn.user_info?.name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-textSecondary text-xs">Vehicle</div>
                      <div>{txn.vehicle_info ? `${txn.vehicle_info.year} ${txn.vehicle_info.make} ${txn.vehicle_info.model}` : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-textSecondary text-xs">Amount</div>
                      <div className="text-primary font-bold">${txn.amount?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-textSecondary text-xs">Status</div>
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-bold uppercase ${
                        txn.payment_status === 'completed' ? 'bg-green-600' : 'bg-yellow-600'
                      }`}>{txn.payment_status}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Car Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold mb-4">Car Requests</h2>
            {carRequests.length === 0 ? (
              <div className="glass p-8 text-center text-textSecondary">No car requests yet</div>
            ) : (
              carRequests.map(request => (
                <div key={request.request_id} className="glass p-6 rounded-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-xl mb-2">
                        {request.make} {request.model} ({request.year_min} - {request.year_max})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                        <div><span className="text-textSecondary">User:</span> <span className="ml-2">{request.user_name}</span></div>
                        <div><span className="text-textSecondary">Email:</span> <span className="ml-2">{request.user_email}</span></div>
                        <div><span className="text-textSecondary">Budget:</span> <span className="ml-2 font-bold text-primary">${request.budget?.toLocaleString()}</span></div>
                        <div><span className="text-textSecondary">Location:</span> <span className="ml-2">{request.location}</span></div>
                        <div><span className="text-textSecondary">Submitted:</span> <span className="ml-2">{new Date(request.created_at).toLocaleDateString()}</span></div>
                        {request.assigned_dealer_name && (
                          <div><span className="text-textSecondary">Handled by:</span> <span className="ml-2 font-bold text-blue-400">{request.assigned_dealer_name}</span></div>
                        )}
                        {request.contacted_at && (
                          <div><span className="text-textSecondary">Contacted:</span> <span className="ml-2">{new Date(request.contacted_at).toLocaleDateString()}</span></div>
                        )}
                        {request.fulfilled_at && (
                          <div><span className="text-textSecondary">Fulfilled:</span> <span className="ml-2">{new Date(request.fulfilled_at).toLocaleDateString()}</span></div>
                        )}
                      </div>
                      {request.additional_notes && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-sm text-textSecondary">
                            <strong>Notes:</strong> {request.additional_notes}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex flex-col items-end space-y-2">
                      <span className={`px-4 py-2 rounded-sm text-sm font-bold uppercase ${
                        request.status === 'pending' ? 'bg-yellow-600' :
                        request.status === 'contacted' ? 'bg-blue-600' : 'bg-green-600'
                      }`}>
                        {request.status}
                      </span>
                      {request.status !== 'fulfilled' && (
                        <div className="flex flex-col space-y-2 w-full">
                          {request.status === 'pending' && (
                            <button
                              onClick={() => updateRequestStatus(request.request_id, 'contacted')}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-xs font-bold transition-colors"
                            >
                              Mark Contacted
                            </button>
                          )}
                          <button
                            onClick={() => updateRequestStatus(request.request_id, 'fulfilled')}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-sm text-xs font-bold transition-colors"
                          >
                            Mark Fulfilled
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Reservations Tab */}
        {activeTab === 'reservations' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold mb-4">All Reservations</h2>
            {reservations.length === 0 ? (
              <div className="glass p-8 text-center text-textSecondary">
                No reservations yet
              </div>
            ) : (
              <div className="space-y-4">
                {reservations.map((reservation) => {
                  const getStatusColor = (status) => {
                    switch(status) {
                      case 'pending': return 'bg-yellow-600';
                      case 'approved': return 'bg-blue-600';
                      case 'rejected': return 'bg-red-600';
                      case 'completed': return 'bg-green-600';
                      default: return 'bg-gray-600';
                    }
                  };

                  return (
                    <div key={reservation.reservation_id} className="glass p-6 rounded-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${getStatusColor(reservation.status)}`}>
                              {reservation.status}
                            </span>
                            <span className="text-xs text-textSecondary">
                              {new Date(reservation.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <h3 className="font-heading font-bold text-xl mb-2">
                            {reservation.vehicle_year} {reservation.vehicle_make} {reservation.vehicle_model}
                          </h3>
                          
                          <div className="text-3xl font-heading font-bold text-primary mb-3">
                            ${(reservation.reserved_price || reservation.amount || 0).toLocaleString()}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm text-textSecondary mb-4">
                            <div><strong>Buyer:</strong> {reservation.buyer_name || reservation.user_name || 'N/A'}</div>
                            <div><strong>Dealer ID:</strong> {reservation.dealer_id}</div>
                            <div><strong>Reservation ID:</strong> {reservation.reservation_id.substring(0, 16)}...</div>
                            <div><strong>Vehicle ID:</strong> {reservation.vehicle_id}</div>
                          </div>
                          
                          <a
                            href={`/vehicle/${reservation.vehicle_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-surfaceHighlight hover:bg-white/10 text-white rounded-sm font-medium px-4 py-2 transition-colors text-sm"
                          >
                            View Vehicle
                          </a>
                        </div>
                        
                        <div className="ml-6 flex flex-col space-y-2 min-w-[140px]">
                          <div className="text-xs text-textSecondary mb-1 text-center">Admin Override</div>
                          {reservation.status !== 'approved' && (
                            <button
                              onClick={() => updateReservationStatus(reservation.reservation_id, 'approved')}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-sm font-bold transition-colors"
                            >
                              Approve
                            </button>
                          )}
                          {reservation.status !== 'rejected' && (
                            <button
                              onClick={() => updateReservationStatus(reservation.reservation_id, 'rejected')}
                              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm text-sm font-bold transition-colors"
                            >
                              Reject
                            </button>
                          )}
                          {reservation.status !== 'completed' && reservation.status !== 'rejected' && (
                            <button
                              onClick={() => updateReservationStatus(reservation.reservation_id, 'completed')}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-sm text-sm font-bold transition-colors"
                            >
                              Mark Completed
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}


        {/* Payout Subaccounts Tab */}
        {activeTab === 'subaccounts' && (
          <div className="space-y-6">
            <div className="glass rounded-sm overflow-hidden" data-testid="subaccounts-pending-panel">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-xl font-heading font-bold">Dealers Awaiting Payout Subaccount</h2>
                <p className="text-sm text-textSecondary mt-1">
                  These dealers have submitted verified bank details. Provisioning a Paystack subaccount enables direct, split-settlement payouts and unlocks payment acceptance on their listings.
                </p>
              </div>
              {pendingSubaccounts.length === 0 ? (
                <div className="p-8 text-center text-textSecondary">No dealers awaiting subaccount provisioning.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5">
                      <tr className="text-left text-xs uppercase text-textSecondary">
                        <th className="px-4 py-3">Business</th>
                        <th className="px-4 py-3">Dealer</th>
                        <th className="px-4 py-3">Bank</th>
                        <th className="px-4 py-3">Account #</th>
                        <th className="px-4 py-3">Account Name</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingSubaccounts.map((d) => (
                        <tr key={d.user_id} className="border-t border-white/10" data-testid={`subaccount-pending-row-${d.user_id}`}>
                          <td className="px-4 py-3 font-medium">{d.business_name || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{d.name}</div>
                            <div className="text-xs text-textSecondary">{d.email}</div>
                          </td>
                          <td className="px-4 py-3">{d.bank_name}</td>
                          <td className="px-4 py-3 font-mono">{d.account_number}</td>
                          <td className="px-4 py-3">{d.account_name}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => provisionSubaccount(d.user_id)}
                              disabled={provisioningId === d.user_id}
                              data-testid={`provision-subaccount-${d.user_id}`}
                              className="bg-primary hover:bg-primaryHover text-white px-4 py-2 rounded-sm font-bold text-xs disabled:opacity-50"
                            >
                              {provisioningId === d.user_id ? 'Creating…' : 'Create Subaccount'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="glass rounded-sm overflow-hidden" data-testid="subaccounts-active-panel">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-xl font-heading font-bold">Active Payout Subaccounts</h2>
                <p className="text-sm text-textSecondary mt-1">
                  Dealers below already receive their share of every transaction directly via Paystack. JAMOH retains only the platform commission per transaction.
                </p>
              </div>
              {activeSubaccounts.length === 0 ? (
                <div className="p-8 text-center text-textSecondary">No active subaccounts yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5">
                      <tr className="text-left text-xs uppercase text-textSecondary">
                        <th className="px-4 py-3">Business</th>
                        <th className="px-4 py-3">Dealer</th>
                        <th className="px-4 py-3">Bank</th>
                        <th className="px-4 py-3">Account</th>
                        <th className="px-4 py-3">Subaccount Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSubaccounts.map((d) => (
                        <tr key={d.user_id} className="border-t border-white/10">
                          <td className="px-4 py-3 font-medium">{d.business_name || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{d.name}</div>
                            <div className="text-xs text-textSecondary">{d.email}</div>
                          </td>
                          <td className="px-4 py-3">{d.bank_name}</td>
                          <td className="px-4 py-3">
                            <div className="font-mono">{d.account_number}</div>
                            <div className="text-xs text-textSecondary">{d.account_name}</div>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{d.paystack_subaccount_code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Featured Listings Tab */}
        {activeTab === 'featured' && (
          <div className="space-y-6">
            <div className="glass rounded-sm overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-heading font-bold flex items-center space-x-2">
                      <Zap className="w-6 h-6 text-yellow-500" />
                      <span>Featured Vehicle Listings</span>
                    </h2>
                    <p className="text-sm text-textSecondary mt-1">Monitor paid boost campaigns and featured placements</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-heading font-bold text-yellow-500">{boosts.length}</div>
                    <div className="text-xs text-textSecondary">Total Boosts</div>
                  </div>
                </div>
              </div>

              {boosts.length === 0 ? (
                <div className="p-12 text-center text-textSecondary">
                  <Zap className="w-16 h-16 mx-auto mb-4 text-textSecondary opacity-50" />
                  <p>No featured listings yet</p>
                  <p className="text-xs mt-2">Featured listings will appear here when dealers purchase boost packages</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-surfaceHighlight border-b border-white/10">
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">Dealer</th>
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">Vehicle</th>
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">Package</th>
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">Duration</th>
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">Start Date</th>
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">End Date</th>
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">Amount</th>
                        <th className="text-left p-4 text-xs font-heading font-bold uppercase text-textSecondary">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {boosts.map((boost) => {
                        const startDate = new Date(boost.start_date);
                        const endDate = new Date(boost.end_date);
                        const isActive = boost.is_active && new Date() < endDate;
                        
                        return (
                          <tr key={boost.boost_id} className="hover:bg-surfaceHighlight transition-colors">
                            <td className="p-4">
                              <div className="font-medium">{boost.dealer_name || 'Unknown Dealer'}</div>
                              <div className="text-xs text-textSecondary">{boost.dealer_email || ''}</div>
                            </td>
                            <td className="p-4">
                              <div className="font-medium">{boost.vehicle_name || 'Unknown Vehicle'}</div>
                              <div className="text-xs text-textSecondary">ID: {boost.vehicle_id}</div>
                            </td>
                            <td className="p-4">
                              <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${
                                boost.package === 'platinum' ? 'bg-purple-600 text-white' :
                                boost.package === 'premium' ? 'bg-yellow-600 text-white' :
                                'bg-blue-600 text-white'
                              }`}>
                                {boost.package}
                              </span>
                            </td>
                            <td className="p-4 text-sm">{boost.duration_days} days</td>
                            <td className="p-4 text-sm">{startDate.toLocaleDateString()}</td>
                            <td className="p-4 text-sm">{endDate.toLocaleDateString()}</td>
                            <td className="p-4 text-sm font-medium text-green-500">₦{boost.amount?.toLocaleString() || '0'}</td>
                            <td className="p-4">
                              <div className="flex flex-col space-y-1">
                                <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase w-fit ${
                                  boost.payment_status === 'paid' ? 'bg-green-600 text-white' :
                                  boost.payment_status === 'pending' ? 'bg-yellow-600 text-white' :
                                  'bg-red-600 text-white'
                                }`}>
                                  {boost.payment_status}
                                </span>
                                <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase w-fit ${
                                  isActive ? 'bg-green-600/30 text-green-400 border border-green-600' :
                                  'bg-gray-600/30 text-gray-400 border border-gray-600'
                                }`}>
                                  {isActive ? 'Active' : 'Expired'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="glass p-6 rounded-sm">
            <h2 className="text-2xl font-heading font-bold mb-6">Platform Settings</h2>
            
            {/* Exchange Rate Management */}
            <div className="mb-8">
              <h3 className="text-xl font-heading font-bold mb-4 flex items-center space-x-2">
                <DollarSign className="w-6 h-6 text-primary" />
                <span>Exchange Rate Management</span>
              </h3>
              
              <div className="bg-surfaceHighlight p-6 rounded-sm border border-white/10">
                <div className="mb-4">
                  <div className="flex items-baseline space-x-2 mb-2">
                    <span className="text-sm text-textSecondary">Current Exchange Rate:</span>
                    <span className="text-3xl font-heading font-bold text-primary">
                      ₦{exchangeRate.toLocaleString()}
                    </span>
                    <span className="text-textSecondary">per $1 USD</span>
                  </div>
                  <p className="text-xs text-textSecondary">
                    This rate is used for all currency conversions across the platform (marketplace, payments, deposits).
                  </p>
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <label className="block text-sm font-medium mb-2">
                    Update Exchange Rate <span className="text-primary">*</span>
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 flex items-center space-x-2">
                      <span className="text-textSecondary font-bold">₦</span>
                      <input
                        type="number"
                        value={newExchangeRate}
                        onChange={(e) => setNewExchangeRate(e.target.value)}
                        placeholder={exchangeRate.toString()}
                        disabled={updatingRate}
                        className="flex-1 bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
                      />
                      <span className="text-textSecondary">/ $1 USD</span>
                    </div>
                    <button
                      onClick={handleUpdateExchangeRate}
                      disabled={updatingRate || !newExchangeRate}
                      className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updatingRate ? 'Updating...' : 'Update Rate'}
                    </button>
                  </div>
                  <p className="text-xs text-textSecondary mt-2">
                    ⚠️ Changing this rate will affect all future price displays and payment calculations.
                  </p>
                </div>
                
                <div className="bg-blue-900/20 border border-blue-600 p-4 rounded-sm mt-4">
                  <p className="text-sm text-blue-400 font-medium mb-2">💡 Best Practices:</p>
                  <ul className="text-xs text-blue-300 space-y-1 list-disc list-inside">
                    <li>Update this rate regularly to reflect current market rates</li>
                    <li>Consider using a live exchange rate API for production</li>
                    <li>Inform users when significant rate changes occur</li>
                    <li>Keep a log of rate changes for audit purposes</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Site Settings / Company Profile */}
            <div className="mb-8">
              <h3 className="text-xl font-heading font-bold mb-4 flex items-center space-x-2">
                <Shield className="w-6 h-6 text-primary" />
                <span>Site Settings & Company Profile</span>
              </h3>
              
              {siteSettings && (
                <div className="bg-surfaceHighlight p-6 rounded-sm border border-white/10 space-y-6">
                  {/* Company Identity */}
                  <div>
                    <h4 className="font-heading font-bold text-lg mb-4 text-primary">Company Identity</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Company Name <span className="text-primary">*</span></label>
                        <input
                          type="text"
                          value={siteSettings.company_name || ''}
                          onChange={(e) => handleSettingsChange('company_name', e.target.value)}
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Company Description</label>
                        <textarea
                          value={siteSettings.company_description || ''}
                          onChange={(e) => handleSettingsChange('company_description', e.target.value)}
                          rows={3}
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div className="border-t border-white/10 pt-6">
                    <h4 className="font-heading font-bold text-lg mb-4 text-primary">Contact Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">Office Address</label>
                        <input
                          type="text"
                          value={siteSettings.office_address || ''}
                          onChange={(e) => handleSettingsChange('office_address', e.target.value)}
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Phone Number</label>
                        <input
                          type="text"
                          value={siteSettings.phone_number || ''}
                          onChange={(e) => handleSettingsChange('phone_number', e.target.value)}
                          placeholder="+234-800-EXAMPLE"
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">WhatsApp URL</label>
                        <input
                          type="text"
                          value={siteSettings.whatsapp_url || ''}
                          onChange={(e) => handleSettingsChange('whatsapp_url', e.target.value)}
                          placeholder="https://wa.me/..."
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Primary Email</label>
                        <input
                          type="email"
                          value={siteSettings.primary_email || ''}
                          onChange={(e) => handleSettingsChange('primary_email', e.target.value)}
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Secondary Email</label>
                        <input
                          type="email"
                          value={siteSettings.secondary_email || ''}
                          onChange={(e) => handleSettingsChange('secondary_email', e.target.value)}
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="border-t border-white/10 pt-6">
                    <h4 className="font-heading font-bold text-lg mb-4 text-primary">Social Media Links</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Facebook URL</label>
                        <input
                          type="text"
                          value={siteSettings.facebook_url || ''}
                          onChange={(e) => handleSettingsChange('facebook_url', e.target.value)}
                          placeholder="https://facebook.com/..."
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Twitter/X URL</label>
                        <input
                          type="text"
                          value={siteSettings.twitter_url || ''}
                          onChange={(e) => handleSettingsChange('twitter_url', e.target.value)}
                          placeholder="https://twitter.com/..."
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Instagram URL</label>
                        <input
                          type="text"
                          value={siteSettings.instagram_url || ''}
                          onChange={(e) => handleSettingsChange('instagram_url', e.target.value)}
                          placeholder="https://instagram.com/..."
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Legal / Footer Text */}
                  <div className="border-t border-white/10 pt-6">
                    <h4 className="font-heading font-bold text-lg mb-4 text-primary">Legal & Footer Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">RC Number</label>
                        <input
                          type="text"
                          value={siteSettings.rc_number || ''}
                          onChange={(e) => handleSettingsChange('rc_number', e.target.value)}
                          placeholder="123456"
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">License Text</label>
                        <input
                          type="text"
                          value={siteSettings.license_text || ''}
                          onChange={(e) => handleSettingsChange('license_text', e.target.value)}
                          placeholder="Licensed Auto Dealer"
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">Copyright Text</label>
                        <input
                          type="text"
                          value={siteSettings.copyright_text || ''}
                          onChange={(e) => handleSettingsChange('copyright_text', e.target.value)}
                          placeholder="© 2026 Company Name. All rights reserved."
                          className="w-full bg-surface border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="border-t border-white/10 pt-6 flex justify-end">
                    <button
                      onClick={handleSaveSiteSettings}
                      disabled={savingSettings}
                      className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-8 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <Save className="w-5 h-5" />
                      <span>{savingSettings ? 'Saving...' : 'Save Site Settings'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Future settings sections */}
            <div className="bg-surfaceHighlight p-6 rounded-sm border border-white/10">
              <h4 className="font-heading font-bold mb-2">Coming Soon</h4>
              <p className="text-sm text-textSecondary">
                Additional platform settings (payment configurations, email templates, notification preferences) will be available in future updates.
              </p>
            </div>
          </div>
        )}

      </div>

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="reset-modal">
          <div className="glass p-6 rounded-sm max-w-lg w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-heading font-bold flex items-center space-x-2">
                <KeyRound className="w-5 h-5 text-primary" /><span>Password Reset Token</span>
              </h3>
              <button onClick={() => { setResetModal(null); setCopiedToken(false); }} className="text-textSecondary hover:text-white">&times;</button>
            </div>
            <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-2 rounded-sm text-sm">
              {resetModal.message}
            </div>
            <div>
              <label className="block text-xs text-textSecondary mb-1">Reset link for {resetModal.user_email} (expires in {resetModal.expires_in})</label>
              <div className="flex items-center space-x-2">
                <input type="text" readOnly value={`${window.location.origin}/reset-password?token=${resetModal.reset_token}`}
                  className="flex-1 bg-surfaceHighlight border border-white/10 text-white rounded-sm px-3 py-2 text-xs font-mono truncate" data-testid="admin-reset-link" />
                <button onClick={copyResetLink} data-testid="admin-copy-reset"
                  className="bg-surfaceHighlight hover:bg-white/10 border border-white/10 text-white rounded-sm px-3 py-2 transition-colors">
                  {copiedToken ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button onClick={() => { setResetModal(null); setCopiedToken(false); }}
              className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold py-2 transition-colors text-sm">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
